import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  withRouter
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import AuthFooter from './components/footer/AuthFooter';
import AuthHeader from './components/header/AuthHeader';
import AuthSessionRefreshDialog from './components/sessionRefresh/AuthSessionRefreshDialog';
import APIDirectoryContainer from './components/api-directory-home/api-directory-home-page/APIDirectoryContainer';
import ScrollToTop from './components/ScrollToTop';
import GtmCoreDataLayer from './components/analytics/GtmCoreDataLayer';
import {
  TopErrorBoundary,
  RouteErrorBoundary
} from '@cdk-prod/fortellis-radial-error-boundary';
import EntityProvider from './components/entity-context/EntityProvider';
import { EntityProvider as LocalEntityProvider } from './components/entity-context/entityContext';
import NotFound from './components/notfound';
import config from './config/config';
import ApiDetailsContainer from './components/apiDetails/ApiDetailsContainer';
import BundleDetailsContainer from './components/bundleDetails/BundleDetailsContainer';
import { LoginLocationProvider } from './components/locationContext';
import { ThemeProvider } from 'styled-components';
import { fortellisTheme } from 'cdk-radial';
import { Security, LoginCallback } from '@okta/okta-react';
import {
  getAuthContext,
  HideSignOut,
  withAuth
} from '@cdk-prod/fortellis-auth-context';
import { AhaWidget } from '@cdk-prod/fortellis-aha-widget';
import { queryClient } from './react-query/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './app.scss';
import {
  loadWalkMeScript,
  updateFortellisWalkMeData
} from './utils/walkMeHelper';

const env = {
  issuer: process.env.REACT_APP_AUTH_ISSUER,
  clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: ['openid', 'profile', 'email'],
  pkce: false
};
const errorBoundaryProps = {
  app: 'apidocs',
  clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
  supportUrl: config.support.url,
  telemetryUrl: process.env.REACT_APP_CRASH_TELEMETRY_ANON_URL,
  userTelemetryUrl: process.env.REACT_APP_CRASH_TELEMETRY_USER_URL
};

const applicationId = process.env.REACT_APP_PLATFORM_APPLICATIONID_FORTELLIS;
const isAhaEnabled =
  process.env.REACT_APP_FEATURE_AHA_WIDGET_ENABLED === 'true';

const AhaWidgetWithAuth = withAuth(AhaWidget);

const App = props => {
  const [mobile, setMobile] = useState(window.innerWidth <= 1039);
  const [entity, setEntity] = useState({ id: -1 });
  const [authDetails, setAuthDetails] = useState({});

  useEffect(() => {
    if (process.env.REACT_APP_WALKME_SRC) {
      loadWalkMeScript({ walkMeSrc: process.env.REACT_APP_WALKME_SRC });
    }
  }, []);

  useEffect(() => {
    // Update WalkMe analytics when both entity data and user authentication are available
    if (entity.id !== -1 && authDetails?.isAuthenticated) {
      const currAuthUserData = authDetails?.userData;
      updateFortellisWalkMeData({
        userData: currAuthUserData,
        entityData: entity
      });
    }
  }, [entity, authDetails]); // Runs when entity or authentication details change

  useEffect(() => {
    if (config.env !== 'prod') {
      console.warn(`Application is currently running in '${config.env}' mode`);
    }

    const updateMobile = () => {
      const isMobile = window.innerWidth <= 1039;
      if (mobile !== isMobile) {
        setMobile(isMobile);
      }
    };

    window.addEventListener('resize', updateMobile);
    return () => {
      window.removeEventListener('resize', updateMobile);
    };
  }, [mobile]);
  const setEntityContextandAuthDetails = (
    entityContext = { id: -1 },
    auth = {}
  ) => {
    setAuthDetails(auth);
    setEntity(entityContext);
  };

  return (
    <ScrollToTop>
      <ThemeProvider theme={fortellisTheme}>
        <TopErrorBoundary {...errorBoundaryProps}>
          <Security
            oktaAuth={props.oktaAuth}
            restoreOriginalUri={props.restoreOriginalUri}
          >
            <QueryClientProvider client={queryClient}>
              <LocalEntityProvider>
                <LoginLocationProvider>
                  <EntityProvider>
                    <HideSignOut />
                    {isAhaEnabled && (
                      <AhaWidgetWithAuth applicationId={applicationId} />
                    )}
                    <AuthSessionRefreshDialog />
                    <GtmCoreDataLayer
                      entity={entity}
                      authDetails={authDetails}
                    />
                    <AuthHeader
                      mobile={mobile}
                      onAuthAndEntityInit={setEntityContextandAuthDetails}
                    />
                    <RouteErrorBoundary {...errorBoundaryProps}>
                      <Switch>
                        <Route
                          exact
                          path="/"
                          component={APIDirectoryContainer}
                        />
                        <Route
                          exact
                          path="/login/callback"
                          component={LoginCallback}
                        />
                        <Route
                          path="/apis/:id"
                          component={ApiDetailsContainer}
                        />
                        <Route
                          path="/async-apis/:id"
                          component={ApiDetailsContainer}
                        />
                        <Route
                          path="/bundles/:id"
                          component={BundleDetailsContainer}
                        />
                        <Route component={NotFound} />
                      </Switch>
                    </RouteErrorBoundary>
                    <AuthFooter mobile={mobile} />
                  </EntityProvider>
                </LoginLocationProvider>
              </LocalEntityProvider>
              {/* <ReactQueryDevtools /> Please Uncomment to have React Dev Tools */}
            </QueryClientProvider>
          </Security>
        </TopErrorBoundary>
      </ThemeProvider>
    </ScrollToTop>
  );
};

const RootWithRouterAccess = withRouter(
  getAuthContext({ WrappedComponent: App, env })
);

const Root = () => (
  <Router>
    <RootWithRouterAccess />
  </Router>
);

export default Root;
